import { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  HelpCircle,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import {
  useAdminFaqItems,
  useCreateFaqItem,
  useUpdateFaqItem,
  useDeleteFaqItem,
  useSwapFaqOrder,
  type FaqItemRow,
} from '@/lib/admin/faq';
import { HELP_TOPIC_PLACES, normalizeHelpKey } from '@/lib/help-faq';

const inputCls =
  'border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2';
const labelCls = 'font-display text-ink-700 text-[12px] font-semibold';
const textareaCls = `${inputCls} min-h-[7rem] resize-y`;

/**
 * Aide contextuelle (migration 0300) : la question peut aussi s'ouvrir depuis
 * une pastille « ? » de l'app (clé), et/ou rester hors de la FAQ du site.
 */
function ContextFields({
  idPrefix,
  contextKey,
  onContextKey,
  showOnLanding,
  onShowOnLanding,
}: {
  idPrefix: string;
  contextKey: string;
  onContextKey: (v: string) => void;
  showOnLanding: boolean;
  onShowOnLanding: (v: boolean) => void;
}) {
  const place = HELP_TOPIC_PLACES[normalizeHelpKey(contextKey) ?? ''];
  const invalid = contextKey.trim() !== '' && normalizeHelpKey(contextKey) === null;
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
      <div className="flex flex-1 flex-col gap-1">
        <label className={labelCls} htmlFor={`${idPrefix}-key`}>
          Aide contextuelle « ? » dans l’app (clé, facultatif)
        </label>
        <input
          id={`${idPrefix}-key`}
          className={inputCls}
          value={contextKey}
          onChange={(e) => onContextKey(e.target.value)}
          placeholder="Ex. hora"
          list={`${idPrefix}-keys`}
          maxLength={40}
        />
        <datalist id={`${idPrefix}-keys`}>
          {Object.entries(HELP_TOPIC_PLACES).map(([key, where]) => (
            <option key={key} value={key}>
              {where}
            </option>
          ))}
        </datalist>
        <p className={`text-[12px] ${invalid ? 'text-red-600' : 'text-ink-500'}`}>
          {invalid
            ? 'Minuscules, chiffres et tirets seulement.'
            : place
              ? `Pastille « ? » : ${place}`
              : contextKey.trim()
                ? 'Aucune pastille n’utilise encore cette clé dans l’app.'
                : 'Laisse vide pour une question de la FAQ seulement.'}
        </p>
      </div>
      <label className="text-ink-700 flex min-h-[2.75rem] items-center gap-2 font-sans text-[13px]">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={showOnLanding}
          onChange={(e) => onShowOnLanding(e.target.checked)}
        />
        Afficher aussi dans la FAQ du site
      </label>
    </div>
  );
}

export function TutorAdminFaqPage() {
  const { data: items = [], isLoading } = useAdminFaqItems();
  const create = useCreateFaqItem();

  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [contextKey, setContextKey] = useState('');
  const [showOnLanding, setShowOnLanding] = useState(true);
  const keyInvalid = contextKey.trim() !== '' && normalizeHelpKey(contextKey) === null;

  const sorted = items.slice().sort((a, b) => a.display_order - b.display_order);
  const nextOrder = (sorted.at(-1)?.display_order ?? 0) + 10;

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!question.trim() || !answer.trim() || keyInvalid || create.isPending) return;
    create.mutate(
      {
        question,
        answer_md: answer,
        display_order: nextOrder,
        is_published: true,
        context_key: normalizeHelpKey(contextKey),
        show_on_landing: showOnLanding,
      },
      {
        onSuccess: () => {
          setQuestion('');
          setAnswer('');
          setContextKey('');
          setShowOnLanding(true);
        },
      },
    );
  }

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="font-display text-ink-900 flex items-center gap-2 text-[22px] font-extrabold">
          <HelpCircle className="text-primary-500 h-6 w-6" />
          FAQ et aide contextuelle
        </h1>
        <p className="text-ink-500 mt-0.5 font-sans text-[13px]">
          {items.length} question{items.length !== 1 ? 's' : ''} — repliées par défaut sur le site,
          triées par ordre d’affichage. Avec une clé, la réponse s’ouvre aussi depuis une pastille «
          ? » dans l’app.
        </p>
      </div>

      {/* Ajouter une question */}
      <form
        onSubmit={handleCreate}
        className="border-ink-200 mb-6 flex flex-col gap-3 rounded-2xl border bg-white p-4"
      >
        <p className="font-display text-ink-800 text-[14px] font-bold">Ajouter une question</p>
        <div className="flex flex-col gap-1">
          <label className={labelCls} htmlFor="faq-new-q">
            Question
          </label>
          <input
            id="faq-new-q"
            className={inputCls}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ex. Le premier exercice est-il gratuit ?"
            maxLength={300}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelCls} htmlFor="faq-new-a">
            Réponse (markdown : **gras**, *italique*, [lien](url))
          </label>
          <textarea
            id="faq-new-a"
            className={textareaCls}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="La réponse affichée quand on déplie la question…"
            maxLength={4000}
          />
        </div>
        <ContextFields
          idPrefix="faq-new"
          contextKey={contextKey}
          onContextKey={setContextKey}
          showOnLanding={showOnLanding}
          onShowOnLanding={setShowOnLanding}
        />
        {create.error && (
          <p className="text-[13px] text-red-600" role="alert">
            {create.error.message}
          </p>
        )}
        <div>
          <Button
            type="submit"
            size="sm"
            disabled={create.isPending || !question.trim() || !answer.trim() || keyInvalid}
            className="flex items-center gap-1.5"
          >
            <Plus className="h-4 w-4" />
            {create.isPending ? 'Ajout…' : 'Ajouter'}
          </Button>
        </div>
      </form>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : sorted.length === 0 ? (
        <div className="py-16 text-center">
          <HelpCircle className="text-ink-300 mx-auto mb-3 h-10 w-10" />
          <p className="font-display text-ink-600 font-semibold">Aucune question pour l’instant</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {sorted.map((item, i) => (
            <FaqAdminRow key={item.id} item={item} prev={sorted[i - 1]} next={sorted[i + 1]} />
          ))}
        </div>
      )}
    </div>
  );
}

function FaqAdminRow({
  item,
  prev,
  next,
}: {
  item: FaqItemRow;
  prev?: FaqItemRow;
  next?: FaqItemRow;
}) {
  const update = useUpdateFaqItem();
  const del = useDeleteFaqItem();
  const swap = useSwapFaqOrder();

  const [editing, setEditing] = useState(false);
  const [question, setQuestion] = useState(item.question);
  const [answer, setAnswer] = useState(item.answer_md);
  const [contextKey, setContextKey] = useState(item.context_key ?? '');
  const [showOnLanding, setShowOnLanding] = useState(item.show_on_landing);
  const keyInvalid = contextKey.trim() !== '' && normalizeHelpKey(contextKey) === null;

  function save() {
    if (!question.trim() || !answer.trim() || keyInvalid) return;
    update.mutate(
      {
        id: item.id,
        question,
        answer_md: answer,
        context_key: normalizeHelpKey(contextKey),
        show_on_landing: showOnLanding,
      },
      { onSuccess: () => setEditing(false) },
    );
  }

  function remove() {
    if (window.confirm(`Supprimer « ${item.question} » ?`)) del.mutate(item.id);
  }

  const busy = update.isPending || del.isPending || swap.isPending;

  return (
    <div className="border-ink-200 rounded-2xl border bg-white p-4">
      {editing ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className={labelCls} htmlFor={`faq-q-${item.id}`}>
              Question
            </label>
            <input
              id={`faq-q-${item.id}`}
              className={inputCls}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={300}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={labelCls} htmlFor={`faq-a-${item.id}`}>
              Réponse (markdown)
            </label>
            <textarea
              id={`faq-a-${item.id}`}
              className={textareaCls}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              maxLength={4000}
            />
          </div>
          <ContextFields
            idPrefix={`faq-${item.id}`}
            contextKey={contextKey}
            onContextKey={setContextKey}
            showOnLanding={showOnLanding}
            onShowOnLanding={setShowOnLanding}
          />
          {update.error && (
            <p className="text-[13px] text-red-600" role="alert">
              {update.error.message}
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={save}
              disabled={busy || !question.trim() || !answer.trim() || keyInvalid}
            >
              {update.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setQuestion(item.question);
                setAnswer(item.answer_md);
                setContextKey(item.context_key ?? '');
                setShowOnLanding(item.show_on_landing);
                setEditing(false);
              }}
            >
              Annuler
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          {/* Réordonnancement */}
          <div className="flex shrink-0 flex-col">
            <button
              type="button"
              aria-label="Monter"
              disabled={!prev || busy}
              onClick={() =>
                prev &&
                swap.mutate({
                  a: { id: item.id, order: item.display_order },
                  b: { id: prev.id, order: prev.display_order },
                })
              }
              className="text-ink-400 hover:text-ink-700 disabled:opacity-30"
            >
              <ChevronUp className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Descendre"
              disabled={!next || busy}
              onClick={() =>
                next &&
                swap.mutate({
                  a: { id: item.id, order: item.display_order },
                  b: { id: next.id, order: next.display_order },
                })
              }
              className="text-ink-400 hover:text-ink-700 disabled:opacity-30"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>

          <div className="min-w-0 flex-1">
            <p className="font-display text-ink-900 text-[15px] font-semibold">
              {item.question}
              {!item.is_published && (
                <span className="bg-ink-100 text-ink-500 ml-2 rounded-full px-2 py-0.5 text-[11px] font-medium">
                  Brouillon
                </span>
              )}
              {item.context_key && (
                <span className="bg-primary-50 text-primary-700 ml-2 rounded-full px-2 py-0.5 text-[11px] font-medium">
                  ? {item.context_key}
                </span>
              )}
              {!item.show_on_landing && (
                <span className="bg-ink-100 text-ink-500 ml-2 rounded-full px-2 py-0.5 text-[11px] font-medium">
                  Hors site
                </span>
              )}
            </p>
            <p className="text-ink-500 mt-1 line-clamp-2 font-sans text-[13px]">{item.answer_md}</p>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              aria-label={item.is_published ? 'Dépublier' : 'Publier'}
              disabled={busy}
              onClick={() => update.mutate({ id: item.id, is_published: !item.is_published })}
              className="text-ink-400 hover:text-primary-600 p-1.5 disabled:opacity-40"
            >
              {item.is_published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            </button>
            <button
              type="button"
              aria-label="Modifier"
              disabled={busy}
              onClick={() => setEditing(true)}
              className="text-ink-400 hover:text-primary-600 p-1.5 disabled:opacity-40"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Supprimer"
              disabled={busy}
              onClick={remove}
              className="text-ink-400 p-1.5 hover:text-red-600 disabled:opacity-40"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
